import { type Draft, produce } from 'immer';
import _isEqual from 'lodash/isEqual';
import type { Store } from 'redux';

import { registerCacheReset } from 'app-components/utils/core/reselect/selectorCacheRegistry';

export enum ValidationTrigger {
  onChange = 1,
  onBlur = 1 << 1,
  onSubmit = 1 << 2,
}

export interface SingleFieldConfig<State, StoreState, FieldValue> {
  selector: (state: State, store: StoreState) => FieldValue;
  disabledSelector?: (state: State, store: StoreState) => boolean;
  hiddenSelector?: (state: State, store: StoreState) => boolean;
  onValueChange: (draft: Draft<State>, value: FieldValue, store: StoreState) => void | State;
  validation?: Record<
    string,
    {
      triggers: number;
      isValid: (state: State, store: StoreState) => boolean;
      error: string | ((state: State, store: StoreState) => string);
    }
  >;
  helperTextSelector?: (state: State, store: StoreState) => string | undefined; // Is overridden by error text
  label?: string | ((state: State, store: StoreState) => string | undefined);
  onBlur?: (draft: Draft<State>, store: StoreState) => void | State;
}

export interface MultiFieldConfig<State, StoreState, FieldValue, FieldKey extends string | number> {
  selector: (state: State, key: FieldKey, store: StoreState) => FieldValue;
  disabledSelector?: (state: State, key: FieldKey, store: StoreState) => boolean;
  hiddenSelector?: (state: State, key: FieldKey, store: StoreState) => boolean;
  onValueChange: (draft: Draft<State>, key: FieldKey, value: FieldValue, store: StoreState) => void | State;
  selectActiveKeys: (state: State, store: StoreState) => FieldKey[];
  validation?: Record<
    string,
    {
      triggers: number;
      isValid: (state: State, key: FieldKey, store: StoreState) => boolean;
      error: string | ((state: State, key: FieldKey, store: StoreState) => string);
    }
  >;
  helperTextSelector?: (state: State, key: FieldKey, store: StoreState) => string | undefined;
  onBlur?: (draft: Draft<State>, key: FieldKey, store: StoreState) => void | State;
  label?: string | ((state: State, key: FieldKey, store: StoreState) => string | undefined);
}

export interface FormBuilderConfig<State, StoreState, Input> {
  init(args: { input: Input; store: StoreState }): State;
}

export interface FormSubmitConfig<State, StoreState, SubmitPayload> {
  submitTransform(state: State, store: StoreState): SubmitPayload;
}

export interface FormActions<
  State,
  StoreState,
  ActionNames extends string,
  Actions extends Record<ActionNames, never>,
> {
  actions: {
    [key in keyof Actions]: (state: State, args: Actions[key], store: StoreState) => State | void;
  };
}

export interface FormFields<
  State,
  StoreState,
  SingleFieldNames extends string,
  SingleFields extends Record<SingleFieldNames, never>,
  MultiFieldNames extends string,
  MultiFields extends Record<MultiFieldNames, { key: string | number; value: never }>,
> {
  singleFields: {
    [key in keyof SingleFields]: SingleFieldConfig<State, StoreState, SingleFields[key]>;
  };
  multiFields: {
    [key in keyof MultiFields]: MultiFieldConfig<State, StoreState, MultiFields[key]['value'], MultiFields[key]['key']>;
  };
}

export interface FormCore<
  State,
  StoreState,
  Input,
  SingleFieldNames extends string,
  SingleFields extends Record<SingleFieldNames, never>,
  MultiFieldNames extends string,
  MultiFields extends Record<MultiFieldNames, { key: string | number; value: never }>,
  ActionNames extends string,
  Actions extends Record<ActionNames, never>,
  SubmitPayload,
> extends FormBuilderConfig<State, StoreState, Input>,
    FormFields<State, StoreState, SingleFieldNames, SingleFields, MultiFieldNames, MultiFields>,
    FormSubmitConfig<State, StoreState, SubmitPayload>,
    FormActions<State, StoreState, ActionNames, Actions> {}

export interface FormBuilder<
  State,
  StoreState,
  Input,
  SingleFieldNames extends string,
  SingleFields extends Record<SingleFieldNames, never>,
  MultiFieldNames extends string,
  MultiFields extends Record<MultiFieldNames, { key: string | number; value: never }>,
  ActionNames extends string,
  Actions extends Record<ActionNames, never>,
> extends FormBuilderConfig<State, StoreState, Input>,
    FormFields<State, StoreState, SingleFieldNames, SingleFields, MultiFieldNames, MultiFields>,
    FormActions<State, StoreState, ActionNames, Actions> {
  addField<FieldName extends string, FieldValue>(
    name: FieldName,
    config: SingleFieldConfig<State, StoreState, FieldValue>,
  ): FormBuilder<
    State,
    StoreState,
    Input,
    SingleFieldNames | FieldName,
    SingleFields & Record<FieldName, FieldValue>,
    MultiFieldNames,
    MultiFields,
    ActionNames,
    Actions
  >;
  addAction<Action extends string, Payload>(
    name: Action,
    handler: (state: Draft<State>, payload: Payload, store: StoreState) => void | State,
  ): FormBuilder<
    State,
    StoreState,
    Input,
    SingleFieldNames,
    SingleFields,
    MultiFieldNames,
    MultiFields,
    ActionNames | Action,
    Actions & Record<Action, Payload>
  >;
  addMultiField<FieldName extends string, FieldValue, FieldKey extends string | number>(
    name: FieldName,
    config: MultiFieldConfig<State, StoreState, FieldValue, FieldKey>,
  ): FormBuilder<
    State,
    StoreState,
    Input,
    SingleFieldNames,
    SingleFields,
    MultiFieldNames | FieldName,
    MultiFields & Record<FieldName, { value: FieldValue; key: FieldKey }>,
    ActionNames,
    Actions
  >;

  build<SubmitPayload>(
    config: FormSubmitConfig<State, StoreState, SubmitPayload>,
  ): FormCore<
    State,
    StoreState,
    Input,
    SingleFieldNames,
    SingleFields,
    MultiFieldNames,
    MultiFields,
    ActionNames,
    Actions,
    SubmitPayload
  >;
}

export function formBuilder<
  State,
  StoreState,
  Input,
  SingleFieldNames extends string,
  SingleFields extends Record<SingleFieldNames, never>,
  MultiFieldNames extends string,
  MultiFields extends Record<MultiFieldNames, { key: string | number; value: never }>,
  ActionNames extends string,
  Actions extends Record<ActionNames, never>,
>(
  config: FormBuilderConfig<State, StoreState, Input>,
): FormBuilder<State, StoreState, Input, never, SingleFields, never, MultiFields, never, Actions> {
  return {
    addField(name, config) {
      const { singleFields, ...rest } = this;
      return { ...rest, singleFields: { ...singleFields, [name]: config } } as any;
    },
    addMultiField(name, config) {
      const { multiFields, ...rest } = this;
      return { ...rest, multiFields: { ...multiFields, [name]: config } } as any;
    },
    addAction(name, handler) {
      const { actions, ...rest } = this;
      return { ...rest, actions: { ...actions, [name]: handler } } as any;
    },

    build(config) {
      const { singleFields, multiFields, actions, init } = this;
      return {
        singleFields,
        multiFields,
        actions,
        init,
        ...config,
      };
    },

    singleFields: {} as {
      [key in keyof SingleFields]: SingleFieldConfig<State, StoreState, SingleFields[key]>;
    },
    multiFields: {} as {
      [key in keyof MultiFields]: MultiFieldConfig<
        State,
        StoreState,
        MultiFields[key]['value'],
        MultiFields[key]['key']
      >;
    },
    actions: {} as {
      [key in keyof Actions]: never;
    },
    ...config,
  };
}

export interface FormManager<
  State,
  StoreState,
  Input,
  SingleFieldNames extends string,
  SingleFields extends Record<SingleFieldNames, never>,
  MultiFieldNames extends string,
  MultiFields extends Record<MultiFieldNames, { key: string | number; value: never }>,
  ActionNames extends string,
  Actions extends Record<ActionNames, never>,
  AdditionalSubmitArg,
> {
  submit: (additionalArg: AdditionalSubmitArg) => Promise<void>;
  subscribe: (cb: () => void) => () => void;

  onFieldChange<FieldName extends SingleFieldNames>(name: FieldName, value: SingleFields[FieldName]): void;
  onFieldBlur<FieldName extends SingleFieldNames>(name: FieldName): void;
  getFieldValue<FieldName extends SingleFieldNames>(name: FieldName): SingleFields[FieldName];
  getFieldDisabled<FieldName extends SingleFieldNames>(name: FieldName): boolean;
  getFieldHidden<FieldName extends SingleFieldNames>(name: FieldName): boolean;
  getFieldError<FieldName extends SingleFieldNames>(name: FieldName): boolean;
  getFieldHelperText<FieldName extends SingleFieldNames>(name: FieldName): string | undefined;
  getFieldLabelText<FieldName extends SingleFieldNames>(name: FieldName): string | undefined;

  onMultiFieldChange<FieldName extends MultiFieldNames>(
    name: FieldName,
    key: MultiFields[FieldName]['key'],
    value: MultiFields[FieldName]['value'],
  ): void;
  onMultiFieldBlur<FieldName extends MultiFieldNames>(name: FieldName, key: MultiFields[FieldName]['key']): void;
  getMultiFieldValue<FieldName extends MultiFieldNames>(
    name: FieldName,
    key: MultiFields[FieldName]['key'],
  ): MultiFields[FieldName]['value'];
  getMultiFieldDisabled<FieldName extends MultiFieldNames>(
    name: FieldName,
    key: MultiFields[FieldName]['key'],
  ): boolean;
  getMultiFieldHidden<FieldName extends MultiFieldNames>(name: FieldName, key: MultiFields[FieldName]['key']): boolean;
  getMultiFieldError<FieldName extends MultiFieldNames>(name: FieldName, key: MultiFields[FieldName]['key']): boolean;
  getMultiFieldHelperText<FieldName extends MultiFieldNames>(
    name: FieldName,
    key: MultiFields[FieldName]['key'],
  ): string | undefined;
  getMultiFieldLabelText<FieldName extends MultiFieldNames>(
    name: FieldName,
    key: MultiFields[FieldName]['key'],
  ): string | undefined;

  fireAction<Action extends ActionNames, Payload extends Actions[Action]>(action: Action, payload: Payload): void;
  runSelector<Result>(selector: (args: { state: State; store: StoreState }) => Result): Result;

  isFormValid(): boolean;
  isFormDirty(): boolean;
  hasFormSubmitted(): boolean;
  resetDirty(): void;
  reinit(input: Input): void;
}

let lastSelectorArgs: { state: unknown; store: unknown } | undefined;
registerCacheReset(() => {
  lastSelectorArgs = undefined;
});

/** Reuses one `{ state, store }` object per pair so memoized selectors can hit their argument cache. */
export function formSelectorArgs<State, StoreState>(
  state: State,
  store: StoreState,
): { state: State; store: StoreState } {
  if (lastSelectorArgs?.state !== state || lastSelectorArgs.store !== store) {
    lastSelectorArgs = { state, store };
  }
  return lastSelectorArgs as { state: State; store: StoreState };
}

export type SubmitHandler<Payload, AdditionalArg = void, Manager = unknown> = (args: {
  payload: Payload;
  additionalArg: AdditionalArg;
  manager: Manager;
}) => Promise<void> | void;

export type FormManagerFactory<
  State,
  StoreState,
  Input,
  SingleFieldNames extends string,
  SingleFields extends Record<SingleFieldNames, never>,
  MultiFieldNames extends string,
  MultiFields extends Record<MultiFieldNames, { key: string | number; value: never }>,
  ActionNames extends string,
  Actions extends Record<ActionNames, never>,
  SubmitPayload,
> = <AdditionalSubmitArg = void>(args: {
  input: Input;
  store: Store<StoreState>;
  /** Skips Redux-backed subscription updates for render-only forms. */
  frozen?: boolean;
  onSubmit: SubmitHandler<
    SubmitPayload,
    AdditionalSubmitArg,
    FormManager<
      State,
      StoreState,
      Input,
      SingleFieldNames,
      SingleFields,
      MultiFieldNames,
      MultiFields,
      ActionNames,
      Actions,
      AdditionalSubmitArg
    >
  >;
}) => FormManager<
  State,
  StoreState,
  Input,
  SingleFieldNames,
  SingleFields,
  MultiFieldNames,
  MultiFields,
  ActionNames,
  Actions,
  AdditionalSubmitArg
>;

export function createManagerFactory<
  State,
  StoreState,
  Input,
  SingleFieldNames extends string,
  SingleFields extends Record<SingleFieldNames, never>,
  MultiFieldNames extends string,
  MultiFields extends Record<MultiFieldNames, { key: string | number; value: never }>,
  ActionNames extends string,
  Actions extends Record<ActionNames, never>,
  SubmitPayload,
>(
  core: FormCore<
    State,
    StoreState,
    Input,
    SingleFieldNames,
    SingleFields,
    MultiFieldNames,
    MultiFields,
    ActionNames,
    Actions,
    SubmitPayload
  >,
): FormManagerFactory<
  State,
  StoreState,
  Input,
  SingleFieldNames,
  SingleFields,
  MultiFieldNames,
  MultiFields,
  ActionNames,
  Actions,
  SubmitPayload
> {
  return ({ input, store, onSubmit, frozen }) => {
    const subscriptions = new Set<() => void>();
    let unsubscribeStore: (() => void) | undefined;
    let lastStoreState: StoreState | undefined;

    function notify() {
      for (const sub of subscriptions.values()) {
        sub();
      }
    }
    // Redux notifies on every dispatch; getters only read form and store state, so skip unchanged roots.
    function onStoreChange() {
      const storeState = store.getState();
      if (storeState === lastStoreState) return;
      lastStoreState = storeState;
      notify();
    }
    let state = core.init({ input, store: store.getState() });
    let initialState = state; // This is a let because we will need to reset the "dirty" state of a form by changing the initialState to match the new state

    let hasSubmitted = false;

    const errors: {
      singleFields: Partial<{
        [key in SingleFieldNames]: Partial<Record<string, boolean>>;
      }>;
      multiFields: Partial<{
        // We convert the field keys to strings to make the types a lot simpler, there really is no typesafety otherwise anyways
        [key in MultiFieldNames]: Partial<Record<string, Partial<Record<string, boolean>>>>;
      }>;
    } = {
      singleFields: {},
      multiFields: {},
    };

    const onChangeValidation = () => {
      const storeState = store.getState();
      for (const field of Object.keys(core.singleFields) as SingleFieldNames[]) {
        const { validation } = core.singleFields[field];
        // Check whether there are any validations for this field
        if (validation) {
          const fieldObject: Partial<Record<string, boolean>> = errors.singleFields[field] ?? {};
          for (const errorKey of Object.keys(validation)) {
            const { isValid, triggers } = validation[errorKey];
            // Check whether we need to be updating error state if one of these is true
            // - If the form has submitted then all validations are on change
            // - If the validation error is triggered by change
            // - The field is valid for this validation
            const hasError = !isValid(state, storeState);
            if (!hasError || hasSubmitted || triggers & ValidationTrigger.onChange) {
              fieldObject[errorKey] = hasError;
            }
          }
          errors.singleFields[field] = fieldObject;
        }
      }
      // Same logic as above but for multifields
      for (const field of Object.keys(core.multiFields) as MultiFieldNames[]) {
        const { validation, selectActiveKeys } = core.multiFields[field];
        // Check whether there are any validations for this field
        if (validation) {
          const fieldObject: Partial<Record<string, Partial<Record<string, boolean>>>> =
            errors.multiFields[field] ?? {};
          // Iterate through all of the active keys so we cover each instance of the multifield
          const activeKeys = selectActiveKeys(state, storeState);
          for (const errorKey of Object.keys(validation)) {
            const errorObject: Partial<Record<string, boolean>> = fieldObject[errorKey] ?? {};
            for (const key of activeKeys) {
              const { isValid, triggers } = validation[errorKey];
              // Check whether we need to be updating error state if one of these is true
              // - If the form has submitted then all validations are on change
              // - If the validation error is triggered by change
              // - The field is valid for this validation
              const hasError = !isValid(state, key, storeState);
              if (!hasError || hasSubmitted || triggers & ValidationTrigger.onChange) {
                errorObject[`${key}`] = hasError;
              }
            }
            fieldObject[errorKey] = errorObject;
          }
          errors.multiFields[field] = fieldObject;
        }
      }
      notify();
    };

    const isFormValid = () =>
      !Object.values<Partial<Record<string, boolean>> | undefined>(errors.singleFields)
        .flatMap((errorRecord) => (errorRecord ? Object.values(errorRecord) : undefined))
        .some(Boolean) &&
      !Object.entries<Partial<Record<string, Partial<Record<string, boolean>>>> | undefined>(errors.multiFields)
        .flatMap(([fieldName, keyedErrors]) => {
          if (!keyedErrors) return undefined;
          // Select active keys and convert to strings
          const activeKeys = core.multiFields[fieldName as MultiFieldNames]
            .selectActiveKeys(state, store.getState())
            .map((key) => `${key}`);
          // Check each key for the current error. Only validate keys that are still active
          return Object.values<Partial<Record<string, boolean>> | undefined>(keyedErrors).flatMap(
            (keyedError) =>
              keyedError && Object.entries(keyedError).some(([key, error]) => activeKeys.includes(key) && error),
          );
        })
        .some(Boolean);

    return {
      isFormValid,
      isFormDirty() {
        return !_isEqual(state, initialState);
      },
      reinit(input) {
        state = core.init({ input, store: store.getState() });
        initialState = state;
        errors.singleFields = {};
        errors.multiFields = {};
        hasSubmitted = false;
        notify();
      },
      resetDirty() {
        initialState = state;
        notify();
      },
      onFieldChange(name, value) {
        state = produce((draft) => core.singleFields[name].onValueChange(draft, value, store.getState()))(state);
        onChangeValidation();
      },
      onFieldBlur(name) {
        const { onBlur } = core.singleFields[name];
        if (onBlur) {
          state = produce((draft) => onBlur(draft, store.getState()))(state);
          onChangeValidation();
        }
      },
      getFieldValue(name) {
        return core.singleFields[name].selector(state, store.getState());
      },
      getFieldDisabled(name) {
        return core.singleFields[name].disabledSelector?.(state, store.getState()) ?? false;
      },
      getFieldHidden(name) {
        return core.singleFields[name].hiddenSelector?.(state, store.getState()) ?? false;
      },
      getFieldError(name) {
        const fieldErrors = errors.singleFields[name];
        if (!fieldErrors) return false;
        return Object.values(fieldErrors).some(Boolean);
      },
      getFieldHelperText(name) {
        const fieldConfig = core.singleFields[name];
        let errorText: string | undefined;
        block: {
          const fieldErrors = errors.singleFields[name];
          if (!fieldErrors) break block;
          const errorName = Object.entries(fieldErrors).find(([_key, isError]) => isError)?.[0];
          const validationObject = errorName && fieldConfig.validation?.[errorName];
          if (!validationObject) break block;
          if (typeof validationObject.error === 'string') {
            errorText = validationObject.error;
          } else {
            errorText = validationObject.error(state, store.getState());
          }
        }
        return errorText ?? core.singleFields[name].helperTextSelector?.(state, store.getState());
      },
      getFieldLabelText(name) {
        const fieldConfig = core.singleFields[name];
        if (typeof fieldConfig.label === 'function') {
          return fieldConfig.label(state, store.getState());
        }
        return fieldConfig.label;
      },

      onMultiFieldChange(name, key, value) {
        state = produce((draft) => {
          return core.multiFields[name].onValueChange(draft, key, value, store.getState());
        })(state);
        onChangeValidation();
      },
      onMultiFieldBlur(name, key) {
        const { onBlur } = core.multiFields[name];
        if (onBlur) {
          state = produce((draft) => onBlur(draft, key, store.getState()))(state);
          onChangeValidation();
        }
      },
      getMultiFieldValue(name, key) {
        return core.multiFields[name].selector(state, key, store.getState());
      },
      getMultiFieldDisabled(name, key) {
        return core.multiFields[name].disabledSelector?.(state, key, store.getState()) ?? false;
      },
      getMultiFieldHidden(name, key) {
        return core.multiFields[name].hiddenSelector?.(state, key, store.getState()) ?? false;
      },
      getMultiFieldError(name, key) {
        const fieldErrors = errors.multiFields[name];
        if (!fieldErrors) return false;
        const stringKey = `${key}`;
        for (const keyedError of Object.values(fieldErrors)) {
          if (keyedError?.[stringKey]) {
            return true;
          }
        }
        return false;
      },
      getMultiFieldHelperText(name, key) {
        const fieldConfig = core.multiFields[name];
        let errorText: string | undefined;
        block: {
          const fieldErrors = errors.multiFields[name];
          if (!fieldErrors) break block;
          const stringKey = `${key}`;
          for (const [errorName, keyedError] of Object.entries(fieldErrors)) {
            if (keyedError?.[stringKey]) {
              const validationObject = errorName && fieldConfig.validation?.[errorName];
              if (!validationObject) break block;
              if (typeof validationObject.error === 'string') {
                errorText = validationObject.error;
                break block;
              } else {
                errorText = validationObject.error(state, key, store.getState());
                break block;
              }
            }
          }
        }
        return errorText ?? fieldConfig.helperTextSelector?.(state, key, store.getState());
      },
      getMultiFieldLabelText(name, key) {
        const fieldConfig = core.multiFields[name];
        if (typeof fieldConfig.label === 'function') {
          return fieldConfig.label(state, key, store.getState());
        }
        return fieldConfig.label;
      },
      runSelector(selector) {
        return selector(formSelectorArgs(state, store.getState()));
      },

      fireAction(action, payload) {
        state = produce((draft) => {
          return core.actions[action](draft, payload, store.getState());
        })(state);
        onChangeValidation();
      },

      async submit(additionalArg: Parameters<typeof onSubmit>[0]['additionalArg']) {
        hasSubmitted = true;
        // Now that hasSubmitted is true this will validate all fields
        onChangeValidation();
        if (isFormValid()) {
          const payload = core.submitTransform(state, store.getState());
          await onSubmit({ payload, additionalArg, manager: this });
        }
      },

      subscribe(cb) {
        subscriptions.add(cb);
        // One store subscription per manager, held while it has listeners.
        if (!frozen && !unsubscribeStore) {
          lastStoreState = store.getState();
          unsubscribeStore = store.subscribe(onStoreChange);
        }
        return () => {
          subscriptions.delete(cb);
          if (subscriptions.size === 0 && unsubscribeStore) {
            unsubscribeStore();
            unsubscribeStore = undefined;
          }
        };
      },
      hasFormSubmitted() {
        return hasSubmitted;
      },
    };
  };
}
